import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';
import Select from '../../../components/base/Select';
import Input from '../../../components/base/Input';
import { supabase, Shipment } from '../../../utils/supabase';
import LoginModal from '../../../components/feature/LoginModal';
import { useAuth } from '../../../hooks/useAuth';

export default function LoginDashboard() {
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; shipment: Shipment | null }>({
    show: false,
    shipment: null
  });
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();
  const { user, login, logout } = useAuth();
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    if (!user) {
      setShowLoginModal(true);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchShipments();
    }
  }, [user]);

  const handleLogin = (userData: any) => {
    login(userData);
    setShowLoginModal(false);
  };

  const fetchShipments = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('shipments')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setShipments(data || []);
    } catch (error) {
      console.error('Error fetching shipments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteShipment = async () => {
    if (!deleteModal.shipment) return;

    try {
      setDeleting(true);
      
      // Delete shipment updates first
      const { error: updatesError } = await supabase
        .from('shipment_updates')
        .delete()
        .eq('tracking_id', deleteModal.shipment.tracking_id);

      if (updatesError) throw updatesError;

      // Delete shipment
      const { error: shipmentError } = await supabase
        .from('shipments')
        .delete()
        .eq('id', deleteModal.shipment.id);

      if (shipmentError) throw shipmentError;

      // Refresh shipments list
      fetchShipments();
      setDeleteModal({ show: false, shipment: null });
    } catch (error) {
      console.error('Error deleting shipment:', error);
      alert('Failed to delete shipment. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  // Show login modal if not authenticated
  if (!user) {
    return (
      <LoginModal 
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLogin={handleLogin}
      />
    );
  }

  const filteredShipments = shipments.filter(shipment => {
    const matchesStatus = statusFilter === 'all' || shipment.status === statusFilter;
    const matchesSearch = shipment.tracking_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         shipment.destination.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         shipment.origin.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Created': return 'bg-blue-100 text-blue-800';
      case 'In Transit': return 'bg-yellow-100 text-yellow-800';
      case 'Delivered': return 'bg-green-100 text-green-800';
      case 'Cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* User Info Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Welcome, {user.full_name}</h2>
            <p className="text-sm text-gray-600">{user.email} • {user.role}</p>
          </div>
          <button
            onClick={logout}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 whitespace-nowrap"
          >
            Logout
          </button>
        </div>

        <div className="mb-8">
          <div className="flex justify-between items-center mb-6">
            <div>
              <p className="text-gray-600">Welcome back! Manage your shipments and track deliveries.</p>
            </div>
            <div className="flex space-x-3">
              <Button
                onClick={() => navigate('/admin/create-shipment')}
                className="whitespace-nowrap"
              >
                <i className="ri-add-line mr-2"></i>
                Create Shipment
              </Button>
              <Button
                onClick={() => navigate('/admin/bulk-upload')}
                variant="outline"
                className="whitespace-nowrap"
              >
                <i className="ri-upload-line mr-2"></i>
                Bulk Upload
              </Button>
              {user?.role === 'admin' && (
                <Button
                  onClick={() => navigate('/admin/user-management')}
                  variant="outline"
                  className="whitespace-nowrap"
                >
                  <i className="ri-team-line mr-2"></i>
                  Manage Users
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-ship-line text-blue-600 text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Shipments</p>
                <p className="text-2xl font-bold text-gray-900">{shipments.length}</p>
              </div>
            </div>
          </Card>
          
          <Card>
            <div className="flex items-center">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <i className="ri-truck-line text-yellow-600 text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">In Transit</p>
                <p className="text-2xl font-bold text-gray-900">
                  {shipments.filter(s => s.status === 'In Transit').length}
                </p>
              </div>
            </div>
          </Card>
          
          <Card>
            <div className="flex items-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Delivered</p>
                <p className="text-2xl font-bold text-gray-900">
                  {shipments.filter(s => s.status === 'Delivered').length}
                </p>
              </div>
            </div>
          </Card>
          
          <Card>
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-add-circle-line text-blue-600 text-xl"></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">New Today</p>
                <p className="text-2xl font-bold text-gray-900">
                  {shipments.filter(s => 
                    new Date(s.created_at).toDateString() === new Date().toDateString()
                  ).length}
                </p>
              </div>
            </div>
          </Card>
        </div>

        <Card>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <h2 className="text-xl font-semibold text-gray-900">All Shipments</h2>
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              <Input
                placeholder="Search by tracking ID, origin, or destination..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="sm:w-80"
              />
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'Created', label: 'Created' },
                  { value: 'In Transit', label: 'In Transit' },
                  { value: 'Delivered', label: 'Delivered' },
                  { value: 'Cancelled', label: 'Cancelled' }
                ]}
              />
              <Link to="/admin/bulk-upload">
                <Button variant="outline">
                  <i className="ri-file-upload-line mr-2"></i>
                  Bulk Upload
                </Button>
              </Link>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <i className="ri-loader-4-line animate-spin text-2xl text-blue-600"></i>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tracking ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Route
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Weight
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredShipments.map((shipment) => (
                    <tr key={shipment.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {shipment.tracking_id}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {shipment.origin} → {shipment.destination}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {shipment.weight} {shipment.mode_of_transportation === 'Air' ? 'kg' : 'cbm'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(shipment.status)}`}>
                          {shipment.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="font-medium">{shipment.created_by || 'N/A'}</div>
                          <div className="text-xs text-gray-500">{shipment.created_by_email || ''}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {shipment.created_at_timestamp ? formatDate(shipment.created_at_timestamp) : formatDate(shipment.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          <Link
                            to={`/admin/shipment/${shipment.tracking_id}`}
                            className="text-blue-600 hover:text-blue-900 cursor-pointer"
                          >
                            View Details
                          </Link>
                          <button
                            onClick={() => setDeleteModal({ show: true, shipment })}
                            className="text-red-600 hover:text-red-900 cursor-pointer"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {filteredShipments.length === 0 && (
                <div className="text-center py-12">
                  <i className="ri-ship-line text-4xl text-gray-400 mb-4"></i>
                  <p className="text-gray-500">No shipments found</p>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Delete Confirmation Modal */}
        {deleteModal.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                    <i className="ri-delete-bin-line text-red-600"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Shipment</h3>
                </div>
                
                <p className="text-gray-600 mb-6">
                  Are you sure you want to delete shipment <strong>{deleteModal.shipment?.tracking_id}</strong>? 
                  This action cannot be undone and will also delete all tracking updates.
                </p>
                
                <div className="flex justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setDeleteModal({ show: false, shipment: null })}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDeleteShipment}
                    loading={deleting}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    Delete Shipment
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
